import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../mock';

const TermsConditions = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationLinks = [
    { name: 'About', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Process', href: '/#process' },
    { name: 'Pricing', href: '/#pricing' },
    { name: 'FAQs', href: '/#faqs' },
    { name: 'Contact', href: '/#contact' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EFEBE9' }}>
      <Helmet>
        <title>Terms & Conditions - TradeMark Registry</title>
        <meta name="description" content="Terms and Conditions for TradeMark Registry trademark registration services. Read our service agreement, pricing terms, client responsibilities, and legal disclaimers." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://trademark-pro-7.preview.emergentagent.com/terms" />
      </Helmet>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#3E2723] shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-white.png" 
                alt="TradeMark Registry Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[#D7CCC8] hover:text-white transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => navigate('/')}
                className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-2 text-[#D7CCC8] hover:text-white transition-colors duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => {
                  navigate('/');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full mt-2 bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl" style={{ marginTop: '64px' }}>
        <h1 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-8">Terms & Conditions</h1>
        
        <Card className="bg-white border-[#D7CCC8]">
          <CardContent className="p-8 md:p-12">
            <p className="text-[#8D6E63] mb-6">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8 text-[#8D6E63]">
              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">1. Agreement to Terms</h2>
                <p className="mb-4">
                  These Terms and Conditions ("Terms") constitute a legally binding agreement between you and {contactInfo.businessName} 
                  ("Company," "we," "our," or "us") concerning your access to and use of our website and trademark registration services.
                </p>
                <p>
                  By accessing or using our services, you agree to be bound by these Terms. If you do not agree with these Terms, 
                  you must not access or use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">2. Services Provided</h2>
                <p className="mb-4">
                  {contactInfo.businessName} provides trademark registration services, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Trademark search and clearance</li>
                  <li>Trademark application preparation and filing with the USPTO</li>
                  <li>Office action response and trademark prosecution</li>
                  <li>Trademark monitoring and enforcement support</li>
                  <li>Consultation and advisory services related to trademark matters</li>
                </ul>
                <p className="mt-4">
                  <strong>Important:</strong> We are not a law firm and do not provide legal advice. Our services are administrative 
                  in nature. For legal advice, you should consult with a licensed attorney.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">3. Service Fees and Payment</h2>
                <h3 className="text-xl font-semibold text-[#3E2723] mb-3">3.1 Pricing</h3>
                <p className="mb-4">
                  Our service fees are clearly outlined in our pricing plans. These fees are separate from and in addition to 
                  government filing fees required by the USPTO.
                </p>

                <h3 className="text-xl font-semibold text-[#3E2723] mb-3">3.2 Government Fees</h3>
                <p className="mb-4">
                  USPTO filing fees are not included in our service fees and must be paid separately. Current government fees 
                  range from $250 to $350 per class of goods/services and are subject to change by the USPTO.
                </p>

                <h3 className="text-xl font-semibold text-[#3E2723] mb-3">3.3 Payment Terms</h3>
                <p className="mb-4">
                  Payment is required before services are rendered unless otherwise agreed in writing. We accept major credit 
                  cards and other payment methods as indicated on our website.
                </p>

                <h3 className="text-xl font-semibold text-[#3E2723] mb-3">3.4 Refund Policy</h3>
                <p>
                  Our refund policy varies by service package. Government filing fees paid to the USPTO are non-refundable. 
                  Service fees may be refundable before work has commenced. Please contact us for specific refund inquiries.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">4. Client Responsibilities</h2>
                <p className="mb-4">
                  As a client, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, complete, and timely information required for trademark application</li>
                  <li>Respond promptly to requests for information or documentation</li>
                  <li>Review all application materials carefully before submission</li>
                  <li>Inform us of any changes to your contact information</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Ensure you have the right to use and register the proposed trademark</li>
                  <li>Not use the trademark in a manner that infringes on others' rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">5. No Guarantee of Registration</h2>
                <p className="mb-4">
                  <strong>IMPORTANT:</strong> While we strive to provide excellent service and have a high success rate, 
                  we cannot guarantee that your trademark application will be approved by the USPTO. The final decision on 
                  trademark registration rests solely with the USPTO.
                </p>
                <p className="mb-4">
                  Factors that may affect approval include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Similarity to existing registered or pending trademarks</li>
                  <li>Descriptiveness or genericness of the proposed mark</li>
                  <li>Compliance with USPTO rules and regulations</li>
                  <li>Proper use of the mark in commerce</li>
                  <li>Other legal or procedural issues</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">6. Intellectual Property Rights</h2>
                <p className="mb-4">
                  You retain all rights to your trademark and related intellectual property. We claim no ownership rights to 
                  your trademarks or business information.
                </p>
                <p>
                  All content on our website, including text, graphics, logos, and software, is the property of {contactInfo.businessName} 
                  and is protected by copyright and other intellectual property laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">7. Confidentiality</h2>
                <p className="mb-4">
                  We will maintain the confidentiality of your information in accordance with our Privacy Policy. However, 
                  please note that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Trademark applications become part of the public record once filed with the USPTO</li>
                  <li>We may be required to disclose information pursuant to legal obligations</li>
                  <li>Information may be shared with service providers necessary to deliver our services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">8. Limitation of Liability</h2>
                <p className="mb-4">
                  To the fullest extent permitted by law, {contactInfo.businessName} shall not be liable for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Damages resulting from USPTO decisions or actions</li>
                  <li>Delays in processing beyond our control</li>
                  <li>Third-party actions or claims</li>
                </ul>
                <p className="mt-4">
                  Our total liability to you for any claim arising from our services shall not exceed the amount you paid 
                  for those services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">9. Indemnification</h2>
                <p>
                  You agree to indemnify and hold harmless {contactInfo.businessName} and its officers, employees, and agents 
                  from any claims, damages, losses, or expenses (including attorney fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Your use of our services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your trademark or its use</li>
                  <li>Any information you provide to us</li>
                  <li>Your violation of any third-party rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">10. Termination</h2>
                <p className="mb-4">
                  We reserve the right to terminate or suspend our services to you at any time for any reason, including 
                  violation of these Terms. You may also terminate our services by providing written notice.
                </p>
                <p>
                  Upon termination, you remain responsible for any outstanding fees, and we will provide you with copies 
                  of your application materials.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">11. Modifications to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
                  to our website. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">12. Governing Law and Jurisdiction</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the State of California, 
                  without regard to its conflict of law provisions. Any disputes arising from these Terms or our services 
                  shall be resolved in the state or federal courts located in Los Angeles County, California.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">13. Dispute Resolution</h2>
                <p className="mb-4">
                  In the event of any dispute, both parties agree to first attempt to resolve the matter through good faith 
                  negotiation. If negotiation fails, the dispute may be submitted to mediation before pursuing litigation.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">14. Severability</h2>
                <p>
                  If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall 
                  continue in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">15. Entire Agreement</h2>
                <p>
                  These Terms, together with our Privacy Policy and any service agreements, constitute the entire agreement 
                  between you and {contactInfo.businessName} regarding our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">16. Contact Information</h2>
                <p className="mb-4">
                  If you have questions about these Terms & Conditions, please contact us:
                </p>
                <div className="bg-[#EFEBE9] p-6 rounded-lg">
                  <p className="mb-2"><strong>{contactInfo.businessName}</strong></p>
                  <p className="mb-2">{contactInfo.address}</p>
                  <p className="mb-2">Email: {contactInfo.email}</p>
                  <p>Phone: {contactInfo.phone}</p>
                </div>
              </section>

              <section className="mt-8 p-6 bg-[#EFEBE9] rounded-lg">
                <p className="font-semibold text-[#3E2723]">
                  By using our services, you acknowledge that you have read, understood, and agree to be bound by these 
                  Terms and Conditions.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 mt-12" style={{ backgroundColor: '#3E2723' }}>
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-[#D7CCC8] text-sm">
            © {new Date().getFullYear()} {contactInfo.businessName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
